// DropdownControl.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Control } from '../ControlRenderer';
import { evaluateTemplate, get, set } from '../utils/Utils';
import { Select, MenuItem, InputLabel, FormControl, Typography, TextField, Checkbox, ListItemText, Autocomplete, FormHelperText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormModeType, useFormData } from '../../../components/FormDataProvider';
import { useValidation } from '@/components/ValidationProvider';
import { useAuth } from '@/components/AuthProvider';

/**
 * Represents a dropdown control with properties to manage its source items,
 * selection mode, and autocomplete feature. Extends the base Control interface.
 */
export interface DropdownControl extends Control {
  /**
   * A string used as a key to identify the list of items associated with this dropdown.
   * It helps in rendering or fetching the dropdown's data source.
   */
  itemsKey: string;

  /**
   * Represents the actual items that populate the dropdown.
   * This can be any enumerable set that the dropdown will iterate over to display available options.
   */
  items: any;

  /**
   * Allows support for multi-selection within the dropdown.
   * If true, users can select multiple items simultaneously. (Optional)
   */
  multiple?: boolean;

  /**
   * Switches the dropdown to autocomplete mode, enabling users to search and select
   * items based on partial input matches. This enhances usability for dropdowns with large datasets. (Optional)
   */
  autocomplete?: boolean;

  /**
   * Allows to set null value in the dropdown list
   * If true, an empty item is added to the dropdown list to be able to select null value. (Optional)
   */
  nullable?: boolean;

  /**
   * Allows to specify a placeholder string in the dropdown control (Optional)
   */
  placeholder?: string;

  /**
   * Allows to display codes along with the values (Optional)
   */
  displayCodes?: boolean;
}

interface DropdownControlProps {
  control: Control;
  data: any;
  onDataChange: (newData: any) => void;
  mode: FormModeType;
}

const DropdownControl: React.FC<DropdownControlProps> = ({ control, data, onDataChange, mode }) => {
  const { errors, rule, validateData, setDataContext } = useValidation();
  const theme = useTheme();
  const { token } = useAuth();
  const handleFieldChange = (fieldName: string, value: string | string[] | null) => {
    setSelectedValue(value);
    if (value === '') {
      set(data, fieldName, null);
      onDataChange(null);
    }
    else {
      set(data, fieldName, value);
      onDataChange(data);
    }
    validateData();
  };

  const { formData, prepareLookupData } = useFormData();
  const multiple = (control as DropdownControl).multiple ?? false;
  const autocomplete = (control as DropdownControl).autocomplete ?? false;
  const [selectedValue, setSelectedValue] = useState<any>(multiple? [] : '');

  const errorMessage = errors && control.dataKey ? errors[control.dataKey] : undefined;

  const dropdownControl = control as DropdownControl;

  useEffect(() => {
    const getInitialSelectedValue = () => {
      let currentValue = get(data, control.dataKey ?? '');
      if (currentValue === undefined) {
        currentValue = null;
      }
      return multiple
        ? (Array.isArray(currentValue) ? currentValue : [])
        : (Array.isArray(currentValue) ? '' : currentValue);
    }
    setSelectedValue(getInitialSelectedValue())
  }, [control.dataKey, data, multiple]);

  useEffect(() => {
    if (data) {
      setDataContext(data);
    }
  }, [data, setDataContext]);

  const itemsKey = useMemo(() => {
    if (dropdownControl.itemsKey) {
      return evaluateTemplate(dropdownControl.itemsKey, data, formData);
    }
    return null;
  }, [dropdownControl.itemsKey, data, formData]);

  useEffect(() => {
    // we need to wait for token before retrieving the lookups
    if (itemsKey && token) {
      prepareLookupData(itemsKey, "", undefined);
    }
  }, [token, itemsKey, prepareLookupData]);

  let options = dropdownControl.items ?? get(formData, itemsKey ?? '', []);
  let currentMode = control.mode ? control.mode : mode;
  const className = `eform-text-${currentMode === 'view' ? 'readonly-' : ''}${theme.palette.mode}`;
  const minWidthStyle = control.minWidth ? { minWidth: control.minWidth } : {};

  if (typeof options === 'string') {
    // in case of plain text this is an info message
    return (<TextField
      variant="standard"
      type="text"
      className={className}
      fullWidth
      defaultValue={options}
      inputProps={{ readOnly: true }}
      style={minWidthStyle}
    />)
  }

  // Add a nullable option if specified
  if (dropdownControl.nullable) {
    options = [[null, '']].concat(options); // Append an empty string for display purposes
  }

  // Sort options alphabetically case-insensitive based on display label
  options = options.slice().sort((a: any, b: any) => a[1].toString().localeCompare(b[1].toString(), undefined, { sensitivity: 'base' }));

  // Function to get value by key
  const getValueByKey = (key: any) => {
    //console.log('DropdownControl.getValueByKey');
    //console.log(options);
    //console.log(key);
    if (key === null) return ''; // Return an empty string for null
    for (let i = 0; i < options.length; i++) {
      if (options[i][0] === key) {
        return options[i][1]; // Return the value corresponding to the key
      }
    }
    return null; // Return null if key is not found
  };

  // Function to get display value for multiple selection
  const getDisplayValues = (keys: string[]) => {
    return keys.map((key) => getValueByKey(key)).join(', ');
  };

  if (currentMode === 'label') {
    return (
      <Typography className='eform-labelmode'>
        {multiple && Array.isArray(selectedValue) ? getDisplayValues(selectedValue) : getValueByKey(selectedValue)}
      </Typography>
    )
  }

  return (
    <div key={control.name} style={{ ...minWidthStyle }}>
      {currentMode !== 'view' ? (
        options && autocomplete ? (
          errorMessage ? (
            <FormControl variant="standard" error><Autocomplete // Render Autocomplete component if autocomplete is true
              multiple={multiple}
              className={`eform-dropdown ${className}`}
              clearOnEscape
              options={options} // Extract labels for options
              getOptionLabel={(option) => option[1]} // Set getOptionLabel prop
              value={multiple ? options.filter((option: any[]) => selectedValue.includes(option[0])) : options.find((option: any[]) => option[0] === selectedValue) || null}
              onChange={(event, newValue) => handleFieldChange(control.dataKey ?? '', multiple ? newValue.map((item: any[]) => item[0]) : newValue ? newValue[0] : '')}
              renderInput={(params) =>
                <TextField
                  {...params}
                  placeholder={dropdownControl.placeholder}
                  label={control.label}
                  variant="standard"
                  style={minWidthStyle}
                />}
            /><FormHelperText>{errorMessage}</FormHelperText></FormControl>
          ) : (
            <Autocomplete // Render Autocomplete component if autocomplete is true
              multiple={multiple}
              className={`eform-dropdown ${className}`}
              clearOnEscape
              options={options} // Extract labels for options
              getOptionLabel={(option) => option[1]} // Set getOptionLabel prop
              value={multiple ? options.filter((option: any[]) => selectedValue.includes(option[0])) : options.find((option: any[]) => option[0] === selectedValue) || null}
              onChange={(event, newValue) => handleFieldChange(control.dataKey ?? '', multiple ? newValue.map((item: any[]) => item[0]) : newValue ? newValue[0] : '')}
              renderInput={(params) =>
                <TextField
                  {...params}
                  placeholder={dropdownControl.placeholder}
                  label={control.label}
                  variant="standard"
                  style={minWidthStyle}
                />}
            />
          )
        ) : (
          errorMessage ? (
            <FormControl variant="standard" error><Select
              multiple={multiple}
              className={`eform-dropdown ${className}`}
              MenuProps={{ disableScrollLock: true }}
              label={control.label}
              error
              name={control.name}
              value={selectedValue}
              onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value as string | string[])}
              renderValue={(selected) => multiple && Array.isArray(selected) ? getDisplayValues(selected) : getValueByKey(selected)}
              style={minWidthStyle}
            >
              {options && options.map((option: Array<any>, index: React.Key | null | undefined) => (
                <MenuItem key={index} value={option[0]} className="eform-dropdown-menuitem">
                  {multiple && <Checkbox checked={Array.isArray(selectedValue) && selectedValue.includes(option[0])} />}
                  <ListItemText primary={option[1]} />
                </MenuItem>
              ))}
            </Select><FormHelperText>{errorMessage}</FormHelperText></FormControl>
          ) : (
            <Select
              multiple={multiple}
              className={`eform-dropdown ${className}`}
              MenuProps={{ disableScrollLock: true }}
              label={control.label}
              name={control.name}
              value={selectedValue}
              onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value as string | string[])}
              renderValue={(selected) => multiple && Array.isArray(selected) ? getDisplayValues(selected) : getValueByKey(selected)}
              style={minWidthStyle}
            >
              {options && options.map((option: Array<any>, index: React.Key | null | undefined) => (
                <MenuItem key={index} value={option[0]} className="eform-dropdown-menuitem">
                  {multiple && <Checkbox checked={Array.isArray(selectedValue) && selectedValue.includes(option[0])} />}
                  <ListItemText primary={option[1]} />
                </MenuItem>
              ))}
            </Select>
          ))
      ) : (
        <TextField
          variant="standard"
          type="text"
          className={className}
          fullWidth
          defaultValue={multiple && Array.isArray(selectedValue) ? getDisplayValues(selectedValue) : getValueByKey(selectedValue)}
          inputProps={{ readOnly: true }}
          style={minWidthStyle}
        />
      )}
    </div>
  );
};

export default DropdownControl;
